import React, { useEffect, useState } from "react";

import { InfoSmIcon, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useNotification } from "Containers/Notifications";
import Button, { ButtonWrapper, BackLink, IconControl, Link } from "ds/Button";
import * as Dialog from "ds/Dialog";
import Modal from "ds/Modal";
import Loading from "ds/PageLoading";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { environmentSelector } from "Reducers/environment";
import { clearEnvironmentVariableError } from "Reducers/environment/settings/variable";
import { useAddVariable } from "Reducers/environment/settings/variable/hooks/useAddVariable";
import { useDeleteVariable } from "Reducers/environment/settings/variable/hooks/useDeleteVariable";
import { useGetVariable } from "Reducers/environment/settings/variable/hooks/useGetVariable";
import { useUpdateVariable } from "Reducers/environment/settings/variable/hooks/useUpdateVariable";
import { useDeploymentType } from "Reducers/project/project/hooks/useDeploymentType";
import { getCommonError } from "Reducers/sliceFactory";
import { VariableForm } from "src/organization/common/components";
import { useAppDispatch } from "Store/hooks";

import * as S from "./EnvironmentVariable.styles";

const EnvironmentVariable = ({ isNew = false }: { isNew?: boolean }) => {
  const notify = useNotification();
  const { organizationId, projectId, variableId, environmentId } =
    useDecodedParams<{
      organizationId: string;
      projectId: string;
      variableId: string;
      environmentId: string;
    }>();
  const intl = useIntl();
  const navigate = useNavigate();
  const [isChanged, setIsChanged] = useState(false);
  const [cancelSignal, dispatchCancelSignal] = useState(false);
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const environment = useSelectorWithUrlParams(environmentSelector);
  const {
    variable,
    setVariable,
    getOrigin,
    isOverridingFrom,
    loading,
    errors,
    status
  } = useGetVariable({
    organizationId,
    projectId,
    variableId,
    environmentId
  });
  const addVariable = useAddVariable({
    organizationId,
    projectId,
    environmentId
  });
  const updateVariable = useUpdateVariable({
    organizationId,
    projectId,
    variable: variable!,
    environmentId,
    callback: data => {
      setVariable(data);
      dispatchCancelSignal(!cancelSignal);
    }
  });
  const deleteVariable = useDeleteVariable({
    organizationId,
    projectId,
    variable: variable!,
    environmentId
  });
  const { isAutomaticDeploymentEnabled, shouldShowDeploymentType } =
    useDeploymentType();

  const dispatch = useAppDispatch();

  const [envSettingsVariablesUrl, envSettingsVariableUrl] = useUrls(
    [
      "organization.project.environment.settings.variables",
      "organization.project.environment.settings.variables.variable"
    ],
    {
      variableId: variableId ?? ""
    }
  );
  const isAutomaticDisabled =
    shouldShowDeploymentType && !isAutomaticDeploymentEnabled;

  async function handleSave(data) {
    setIsChanged(false);
    if (isNew && addVariable) {
      const result = await addVariable(data);
      // @ts-expect-error inconsistent ts
      const errApi = result.payload?.error;
      const error = typeof errApi === "string" ? { message: errApi } : errApi;

      const commonError = getCommonError(error).error;
      const errorMessage = commonError?.message;

      if (result.type.includes("rejected")) {
        notify.error({
          title: intl.formatMessage({
            id: "settings.variable.notification.create.rejected.title"
          }),
          description: errorMessage
        });
        return;
      }

      notify.success({
        title: intl.formatMessage({
          id: "settings.variable.notification.create.success.title"
        }),
        description: intl.formatMessage({
          id: isAutomaticDisabled
            ? "settings.environment.variable.notification.create.automatic_disabled.success.description"
            : "settings.environment.variable.notification.create.success.description"
        })
      });

      navigate(envSettingsVariablesUrl);
    }
    if (variable) {
      await updateVariable(data);
      notify.success({
        title: intl.formatMessage({
          id: "settings.variable.notification.create.success.title"
        }),
        description: intl.formatMessage({
          id: isAutomaticDisabled
            ? "settings.environment.variable.notification.update.automatic_disabled.success.description"
            : "settings.environment.variable.notification.update.success.description"
        })
      });
    }
  }

  async function handleDelete() {
    setShouldShowDeleteModal(false);

    const result = await deleteVariable();

    if (result.type.includes("fulfilled")) {
      notify.success({
        title: intl.formatMessage({
          id: "settings.variable.notification.delete.success.title"
        }),
        description: intl.formatMessage({
          id: isAutomaticDisabled
            ? "settings.environment.variable.notification.delete.automatic_disabled.success.description"
            : "settings.environment.variable.notification.delete.success.description"
        })
      });

      navigate(envSettingsVariablesUrl);
    }
  }

  function handleCancel() {
    navigate(envSettingsVariablesUrl);
  }

  useEffect(() => {
    dispatch(clearEnvironmentVariableError());
  }, [dispatch]);

  return (
    <div>
      <BackLink
        id="go-back-environment-variable"
        to={envSettingsVariablesUrl}
        from={envSettingsVariableUrl}
      >
        {intl.formatMessage({ id: "settings.environment.variables.back_to" })}
      </BackLink>
      <S.EnvVariableTitle tag="h2" id="settings-heading">
        {isNew
          ? intl.formatMessage({ id: "settings.environment.variables.create" })
          : variable?.name}
      </S.EnvVariableTitle>
      <S.PageDescription>
        <Typography.BodyText size="small">
          {intl.formatMessage({
            id: "settings.environment.variables.description"
          })}
        </Typography.BodyText>
        <S.NoteWrapper>
          <Dialog.Root>
            <Dialog.Trigger>
              <IconControl aria-label="info-trigger-vars-description">
                <InfoSmIcon />
              </IconControl>
            </Dialog.Trigger>
            <S.Content>
              <Typography.BodyText size="small" weight="semiBold">
                {intl.formatMessage({
                  id: "settings.variables.variables"
                })}
              </Typography.BodyText>
              <Typography.BodyText size="small">
                {intl.formatMessage(
                  {
                    id: "settings.variables.details"
                  },
                  {
                    code: txt => <S.Code variant="inline">{txt}</S.Code>,
                    br: <br />,
                    appEnvironmentVariableName: intl.formatMessage({
                      id: "app_environment_variable_name"
                    })
                  }
                )}
              </Typography.BodyText>
            </S.Content>
          </Dialog.Root>
          <div>
            {intl.formatMessage<React.ReactNode>(
              { id: "settings.project.variables.note" },
              {
                link: txt => (
                  <Link
                    href={intl.formatMessage({
                      id: "links.documentation.app.variables"
                    })}
                  >
                    {txt}
                  </Link>
                )
              }
            )}
          </div>
        </S.NoteWrapper>
      </S.PageDescription>
      {(loading || (!variable && !errors)) && !isNew ? (
        <Loading />
      ) : (
        <VariableForm
          key={`var-${isNew ? "new" : variableId}-form`}
          onSave={handleSave}
          onCancel={handleCancel}
          onDelete={() => setShouldShowDeleteModal(true)}
          errors={{ error: errors }}
          isLoading={status === "pending"}
          variable={variable}
          isChanged={isChanged}
          isChangedUpdate={() => setIsChanged(true)}
          cancelSignal={cancelSignal}
          isOverridingFrom={!isNew && (isOverridingFrom(variable!) as any)}
          environment={environment}
          originEnv={
            !isNew
              ? isOverridingFrom(variable!) === "environment" ||
                variable?.inherited
                ? getOrigin(variable!)
                : ""
              : ""
          }
          variablePage="environment"
        />
      )}
      <Modal
        isOpen={shouldShowDeleteModal}
        onOpenChange={() => setShouldShowDeleteModal(!shouldShowDeleteModal)}
        handleClose={() => setShouldShowDeleteModal(false)}
        id={variable?.name}
      >
        <Typography.Title tag="h2">
          {intl.formatMessage({ id: "settings.variables.modal.title" })}
        </Typography.Title>
        <Typography.BodyText>
          {intl.formatMessage({
            id: "settings.variables.modal.body"
          })}{" "}
          <strong>{variable?.name}</strong>
        </Typography.BodyText>
        <ButtonWrapper spacing="modal" justifyContent="end">
          <Button
            analyticId="cancel"
            variant="secondary"
            onClick={() => setShouldShowDeleteModal(false)}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button
            analyticId="ssh.confirm.yes"
            id={`delete-${variable?.name}-confirm-modal-button`}
            onClick={handleDelete}
          >
            {intl.formatMessage({ id: "ssh.confirm.yes" })}
          </Button>
        </ButtonWrapper>
      </Modal>
    </div>
  );
};

export default EnvironmentVariable;
